import { useState, useEffect } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar, Tag, Newspaper } from "lucide-react";
import KennelSEO, { breadcrumbSchema } from "./KennelSEO";

const API = process.env.REACT_APP_BACKEND_URL + "/api/kennel";

const INTRO = [
  "W tej zakładce chcemy pokazać Wam codzienne życie naszej hodowli — nie tylko piękne zdjęcia, ale także prawdziwe chwile, emocje i pracę, która stoi za każdym naszym psem.",
  "Będziemy dzielić się tutaj tym, co aktualnie dzieje się w hodowli Wölfe vom Katzenberg: rozwojem szczeniąt, przygotowaniami do miotów, szkoleniami, egzaminami, spacerami, socjalizacją oraz codziennością naszych psów.",
  "Chcemy, abyście mogli zobaczyć, że nasza hodowla to nie przypadek, lecz pasja, odpowiedzialność i ogromna miłość do owczarków niemieckich. Każdy dzień to dla nas praca, obserwacja, troska i budowanie charakteru naszych psów od pierwszych chwil życia.",
  "Tutaj będziemy pokazywać to, co dla nas najważniejsze — życie hodowli od środka.",
];

const formatDate = (d) => {
  if (!d) return "";
  try {
    return new Date(d).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
  } catch { return d; }
};

// Adaptive thumbnail: wide photos fill the 3:2 frame; square/portrait graphics show fully (never cropped)
const CardImage = ({ src, alt }) => {
  const [contain, setContain] = useState(false);
  return (
    <img src={src} alt={alt}
      onLoad={(e) => setContain(e.target.naturalWidth / e.target.naturalHeight < 1.4)}
      className={`w-full h-full ${contain ? "object-contain" : "object-cover"} group-hover:scale-105 transition-transform duration-700`} />
  );
};

const KennelBlog = () => {
  useOutletContext();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/blog`).then(r => r.json()).then(d => { setPosts(Array.isArray(d) ? d : []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  return (
    <div data-testid="blog-page">
      <KennelSEO
        title="Z życia hodowli Wölfe vom Katzenberg | Owczarki niemieckie"
        description="Aktualności z hodowli Wölfe vom Katzenberg: codzienne życie psów, rozwój szczeniąt, szkolenia, socjalizacja, wystawy i przygotowania do miotów owczarków niemieckich."
        canonical="https://wolfevomkatzenberg.pl/z-zycia-hodowli"
        ogImage="https://wolfevomkatzenberg.pl/logo_katzenberg_new.png"
        schema={breadcrumbSchema([{ name: "Start", href: "/" }, { name: "Z życia hodowli", href: "/z-zycia-hodowli" }])}
      />

      {/* Hero / Intro */}
      <section className="relative bg-[#141416] border-b border-[#2A2A2D] px-6 md:px-12 pt-16 pb-14 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 30% 20%, #D4AF37 0%, transparent 45%)" }} />
        <div className="max-w-4xl mx-auto relative">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37]/70 uppercase flex items-center gap-2 mb-5">
              <Newspaper className="w-3.5 h-3.5" /> Aktualności hodowli
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#F5F5F5] leading-[1.05] mb-8" data-testid="blog-h1">
              Z życia hodowli
            </h1>
            <div className="w-16 h-px bg-[#D4AF37]/40 mb-8" />
            <div className="space-y-4 max-w-3xl">
              {INTRO.map((p, i) => (
                <p key={i} className="text-[#B8B8B8] text-sm md:text-base leading-[1.9]">{p}</p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Posts list */}
      <section className="px-6 md:px-12 py-16 bg-[#0B0B0D]">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <p className="text-[#B8B8B8]/40 text-sm">Wczytywanie…</p>
          ) : posts.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-[#2A2A2D] bg-[#141416]/40" data-testid="blog-empty">
              <Newspaper className="w-10 h-10 text-[#D4AF37]/40 mx-auto mb-6" />
              <h2 className="font-serif text-xl md:text-2xl text-[#F5F5F5] mb-3">Pierwsze wpisy z życia hodowli pojawią się wkrótce.</h2>
              <p className="text-[#B8B8B8]/55 text-sm max-w-md mx-auto leading-relaxed">
                Przygotowujemy dla Was aktualności — zdjęcia, historie szczeniąt i codzienność naszych owczarków. Zajrzyj tu wkrótce.
              </p>
              <Link to="/kontakt" className="inline-flex items-center gap-2 mt-8 px-6 py-2.5 border border-[#D4AF37]/30 text-[#D4AF37] text-xs tracking-[0.15em] uppercase hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300">
                Kontakt z hodowlą <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7 md:gap-8" data-testid="blog-grid">
              {posts.map((post, i) => (
                <motion.div key={post.id} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}>
                  <Link to={`/z-zycia-hodowli/${post.slug}`}
                    className="group flex flex-col h-full bg-[#141416] border border-[#2A2A2D] hover:border-[#D4AF37]/40 hover:-translate-y-1 transition-all duration-300 overflow-hidden"
                    data-testid={`blog-card-${post.slug}`}>
                    <div className="aspect-[3/2] overflow-hidden bg-[#0B0B0D] flex items-center justify-center">
                      {post.cover_image ? (
                        <CardImage src={post.cover_image} alt={post.image_alt_text || post.title} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#2A2A2D]"><Newspaper className="w-10 h-10" /></div>
                      )}
                    </div>
                    <div className="p-6 md:p-7 flex flex-col flex-1">
                      <div className="flex items-center gap-3 mb-4 text-[10px] tracking-[0.15em] uppercase">
                        {post.category && <span className="text-[#D4AF37]/80 flex items-center gap-1.5"><Tag className="w-3 h-3" /> {post.category}</span>}
                        {post.date && <span className="text-[#B8B8B8]/40 flex items-center gap-1.5"><Calendar className="w-3 h-3" /> {formatDate(post.date)}</span>}
                      </div>
                      <h3 className="font-serif text-xl text-[#F5F5F5] leading-snug mb-3 group-hover:text-[#D4AF37] transition-colors">{post.title}</h3>
                      {post.excerpt && <p className="text-[#B8B8B8]/70 text-sm leading-relaxed line-clamp-3 mb-6">{post.excerpt}</p>}
                      <span className="mt-auto inline-flex items-center gap-2 text-[#D4AF37] text-[11px] tracking-[0.2em] uppercase">
                        Czytaj więcej <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default KennelBlog;
